import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class DefenderShip implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private worldWidth: number;
  
  // Physics properties
  private thrust: number = 300; // Thrust force
  private maxSpeed: number = 400; // Maximum velocity
  private friction: number = 0.95; // Velocity decay
  private facing: number = 1; // 1 for right, -1 for left
  
  // Shooting properties
  private shootCooldown: number = 0;
  private shootDelay: number = 0.1; // 10 shots per second
  
  // Visual properties
  private thrustVisual: boolean = false;
  private hyperspaceCooldown: number = 0;
  private cameraX: number = 0; // Store camera position for rendering

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    worldWidth: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: 20, y: 12 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.worldWidth = worldWidth;
  }

  public update(deltaTime: number): void {
    // Update cooldowns
    this.shootCooldown -= deltaTime;
    this.hyperspaceCooldown -= deltaTime;
    
    // Apply friction to velocity
    this.velocity.x *= this.friction;
    this.velocity.y *= this.friction;
    
    // Clamp velocity to max speed
    const speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);
    if (speed > this.maxSpeed) {
      this.velocity.x = (this.velocity.x / speed) * this.maxSpeed;
      this.velocity.y = (this.velocity.y / speed) * this.maxSpeed;
    }
    
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // World wraparound (authentic Defender mechanic)
    if (this.position.x < -this.size.x) {
      this.position.x = this.worldWidth;
    } else if (this.position.x > this.worldWidth) {
      this.position.x = -this.size.x;
    }
    
    // Keep ship above ground (simple boundary)
    if (this.position.y > 500) { // Rough ground level
      this.position.y = 500;
      this.velocity.y = Math.min(0, this.velocity.y); // Stop downward movement
    }
    
    // Keep ship below ceiling
    if (this.position.y < 50) {
      this.position.y = 50;
      this.velocity.y = Math.max(0, this.velocity.y); // Stop upward movement
    }
    
    // Reset thrust visual
    this.thrustVisual = false;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    // Calculate screen position
    const screenX = this.position.x - this.cameraX;
    const screenY = this.position.y;
    
    // Don't render if off screen
    if (screenX < -50 || screenX > 850) return;

    const centerX = screenX + this.size.x / 2;
    const centerY = screenY + this.size.y / 2;

    this.drawShipSprite(centerX, centerY);
  }

  public setCameraX(cameraX: number): void {
    this.cameraX = cameraX;
  }

  private drawShipSprite(centerX: number, centerY: number): void {
    // Ship pattern (facing direction affects sprite)
    let pattern: number[][];
    
    if (this.facing > 0) {
      // Facing right
      pattern = [
        [0,0,0,1,1,1,0,0,0,0],
        [0,1,1,1,1,1,1,1,0,0],
        [1,1,1,1,1,1,1,1,1,0],
        [1,1,1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1,1,0],
        [0,1,1,1,1,1,1,1,0,0],
        [0,0,0,1,1,1,0,0,0,0]
      ];
    } else {
      // Facing left
      pattern = [
        [0,0,0,0,1,1,1,0,0,0],
        [0,0,1,1,1,1,1,1,1,0],
        [0,1,1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1,1,1],
        [0,1,1,1,1,1,1,1,1,1],
        [0,0,1,1,1,1,1,1,1,0],
        [0,0,0,0,1,1,1,0,0,0]
      ];
    }

    // Draw the ship
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - 10 + col * 2;
          const y = centerY - 7 + row * 2;
          
          let color = '#62D0FE'; // Light blue
          if (this.hyperspaceCooldown > 0) {
            color = '#FFFFFF'; // Flash white during hyperspace cooldown
          }
          
          this.renderManager.fillRect(x, y, 2, 2, color);
        }
      }
    }
    
    // Draw thrust flame if thrusting
    if (this.thrustVisual) {
      const thrustPattern = this.facing > 0 ? [
        [0,1,0],
        [1,1,1],
        [0,1,0]
      ] : [
        [0,1,0],
        [1,1,1],
        [0,1,0]
      ];
      
      const thrustX = this.facing > 0 ? centerX - 12 : centerX + 10;
      const thrustY = centerY - 3;
      
      for (let row = 0; row < thrustPattern.length; row++) {
        for (let col = 0; col < thrustPattern[row].length; col++) {
          if (thrustPattern[row][col]) {
            const x = thrustX + col * 2;
            const y = thrustY + row * 2;
            this.renderManager.fillRect(x, y, 2, 2, '#FFA500'); // Orange thrust
          }
        }
      }
    }
  }

  public thrustLeft(): void {
    this.velocity.x -= this.thrust * 0.016; // Assuming ~60fps
    this.facing = -1;
    this.thrustVisual = true;
  }

  public thrustRight(): void {
    this.velocity.x += this.thrust * 0.016;
    this.facing = 1;
    this.thrustVisual = true;
  }

  public thrustUp(): void {
    this.velocity.y -= this.thrust * 0.016;
    this.thrustVisual = true;
  }

  public thrustDown(): void {
    this.velocity.y += this.thrust * 0.016;
    this.thrustVisual = true;
  }

  public reverse(): void {
    // Authentic Defender mechanic - instant direction reversal
    this.facing *= -1;
    this.velocity.x *= -0.5; // Reduce velocity on reverse
  }

  public fireBullet(): { x: number, y: number, direction: number } | null {
    if (this.shootCooldown > 0) return null;
    
    this.shootCooldown = this.shootDelay;
    
    // Bullet spawns from front of ship
    const bulletX = this.position.x + (this.facing > 0 ? this.size.x : 0);
    const bulletY = this.position.y + this.size.y / 2;
    
    return {
      x: bulletX,
      y: bulletY,
      direction: this.facing
    };
  }

  public hyperspace(newX: number, newY: number): void {
    this.position.x = newX;
    this.position.y = newY;
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.hyperspaceCooldown = 1.0; // Flash white for 1 second
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public getFacing(): number {
    return this.facing;
  }

  public getVelocity(): Vector2 {
    return { ...this.velocity };
  }
}